import React from 'react';
import { Avatar } from '@teetsh/app/src/components/AvatarCard/Avatar';
import SchoolyearLink from '@teetsh/app/src/components/Link/SchoolyearLink';
import tw from 'twin.macro';
import { ILivretStatus } from '@teetsh/app/src/containers/Livrets/types/livret';
import { LivretStatus } from '@teetsh/app/src/containers/Livrets/LivretEval/LivretStatus';
import { LsuStatus } from '@teetsh/app/src/containers/Livrets/types/lsuStatus';
import { LSUStatusBadge } from '@teetsh/app/src/containers/Livrets/LivretEval/LSUStatusBadge';
import SchoolyearButtonLink from '@teetsh/app/src/components/Buttons/SchoolyearButtonLink';
import { ArrowLeftIcon as ArrowLeft } from '@heroicons/react/24/outline';
import livretsUrls from '@teetsh/app/src/containers/Livrets/urls';
import { useTranslation } from 'react-i18next';
import { SortButton } from 'components/SortElevesButton';
import { sortEleves } from 'containers/CarnetsDeNotes/CarnetDetails/sortEleves';
import { getSortFieldsOrder } from 'containers/CarnetsDeNotes/CarnetDetails/Body/getSortFieldsOrder';
import { useGetQuerySort } from 'containers/Livrets/LivretEval/useGetQuerySort';
import { useLocation } from 'react-router-dom';
import { SubSidebar } from 'components/SubSidebar';

interface EleveInfo {
  id: string;
  fullName: string;
  givenName: string;
  familyName: string;
  livretStatus?: ILivretStatus;
  picture?: string;
  lsuStatus?: LsuStatus;
}

interface Props {
  current: string;
  livretEvalId: string;
  eleves: EleveInfo[];
  getEleveLink?: (eleveId: string) => string;
  backLink?: string;
}

export function ElevesSidebar(props: Props) {
  const { eleves, current, livretEvalId, getEleveLink, backLink } = props;
  const location = useLocation();
  const { sortField, desc, onChangeSort } = useGetQuerySort();
  const { t } = useTranslation(['common']);
  return (
    <SubSidebar order={0} collapsedSize={3} minSize={15} maxSize={40}>
      <div tw="pl-2 pr-6 py-2 border-b flex justify-between">
        <SchoolyearButtonLink
          minimal
          withSchoolContext={!backLink}
          icon={ArrowLeft}
          to={`${backLink || `${livretsUrls.livretDetails(livretEvalId)}`}${
            location.search
          }`}
        >
          {t('common:buttons.back')}
        </SchoolyearButtonLink>
        <SortButton
          portal
          fieldName={sortField || 'familyName'}
          onChange={({ fieldName, direction }) =>
            onChangeSort(fieldName, direction === 'ASC' ? 'asc' : 'desc')
          }
          fieldLabel={t(`eleve:fields.${sortField || 'familyName'}`, {
            defaultValue: '',
          })}
          direction={desc ? 'DESC' : 'ASC'}
        />
      </div>
      <nav tw="flex-1 min-h-0 overflow-y-auto pb-6" aria-label="Eleves sidebar">
        <ul role="list" tw="relative z-0 divide-y divide-gray-200">
          {sortEleves(
            eleves,
            getSortFieldsOrder(sortField),
            desc ? 'DESC' : 'ASC',
          ).map(eleve => (
            <li key={eleve.id}>
              <div
                css={[
                  tw`relative px-6 py-5 flex items-center space-x-3 hover:bg-neutral-supershy`,
                  current === eleve.id && tw`bg-neutral-supershy`,
                ]}
              >
                <div tw="shrink-0">
                  <div tw="h-10 w-10 rounded-full">
                    <Avatar
                      picture={eleve?.picture}
                      fill
                      dimensions={{ width: 40, height: 40 }}
                    />
                  </div>
                </div>
                <div tw="flex-1 min-w-0">
                  <SchoolyearLink
                    withSchoolContext={!getEleveLink}
                    to={
                      getEleveLink
                        ? `${getEleveLink(eleve.id)}${location.search}`
                        : `${livretsUrls.livretEleveDetails(
                            livretEvalId,
                            eleve.id,
                          )}${location.search}`
                    }
                    tw="focus:outline-none"
                  >
                    {/* Extend touch target to entire panel */}
                    <span
                      tw="absolute top-0 bottom-0 right-0 left-0"
                      aria-hidden="true"
                    />
                    <p tw="text-sm font-medium text-neutral-intense mb-2">
                      {eleve.fullName}
                    </p>
                    <div tw="flex space-x-2">
                      {eleve.livretStatus && (
                        <LivretStatus status={eleve.livretStatus} />
                      )}
                      {eleve.lsuStatus && (
                        <LSUStatusBadge
                          okLabel="LSU"
                          koLabel="LSU"
                          status={eleve.lsuStatus}
                        />
                      )}
                    </div>
                  </SchoolyearLink>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </nav>
    </SubSidebar>
  );
}
